import { getApiUrl } from '../config';
import { refreshToken } from './auth';

const API_BASE_URL = getApiUrl('');

const getAuthHeader = (): Record<string, string> => {
  const token = localStorage.getItem('access_token');
  if (!token) {
    return {};
  }
  return { 'Authorization': `Bearer ${token.trim()}` };
};

const handleResponse = async <T>(response: Response, originalRequest?: { method: string; url: string; body?: string }): Promise<T> => {
  if (!response.ok) {
    // If we get a 401 and we're not already trying to refresh the token
    if (response.status === 401 && !response.url.includes('/auth/refresh')) {
      try {
        // Try to refresh the token
        await refreshToken();
        // Retry the original request with the new token
        const newResponse = await fetch(originalRequest?.url || response.url, {
          method: originalRequest?.method || 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...getAuthHeader(),
          },
          body: originalRequest?.body,
        });
        return handleResponse<T>(newResponse);
      } catch (error) {
        // If refresh fails, clear tokens and throw error
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        const errorData = await response.json();
        throw new Error(errorData.message || 'Authentication failed');
      }
    }
    const error = await response.json();
    throw new Error(error.message || 'Request failed');
  }

  // If the response is empty (204 No Content), return null
  if (response.status === 204) {
    return null as T;
  }

  return response.json() as Promise<T>;
};

export const api = {
  get: async <T>(endpoint: string): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
    });

    return handleResponse<T>(response, { method: 'GET', url });
  },

  post: async <T>(endpoint: string, data: any): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    const body = JSON.stringify(data);
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body,
    });

    return handleResponse<T>(response, { method: 'POST', url, body });
  },

  put: async <T>(endpoint: string, data: any): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    const body = JSON.stringify(data);
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
      body,
    });

    return handleResponse<T>(response, { method: 'PUT', url, body });
  },

  delete: async <T>(endpoint: string): Promise<T> => {
    const url = `${API_BASE_URL}${endpoint}`;
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        ...getAuthHeader(),
      },
    });

    return handleResponse<T>(response, { method: 'DELETE', url });
  },
}; 